import { delay } from '@passionware/platform-js';
import { promiseState } from '@passionware/platform-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import BookingCostsSummaryTable from 'v1/components/shared/byModel/Booking/BookingCostsSummary/BookingCostsSummaryTable';
import { DialogErrorFooter } from 'v5/patterns/forms/dialogs/DialogErrorFooter';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { useMemoCompare } from 'v5/platform/lang/useMemoCompare';
import RateEditFlyoverUI from '../../../../../v1/components/shared/byModel/Rate/RateEditFlyover/RateEditFlyoverUI';
import { DialogBody } from '../../../../design-system/Dialog';
import { BookingCostsHeader, Tab } from '../_common/BookingCostsHeader';
import { TotalHeader } from '../_common/TotalHeader';
import { ExpenseEntity, ExpenseEntitySpec } from '../types';
import { calculateExpenseSummary } from './calculateExpenseSummary';
import { useExpenseEntity } from './useExpenseEntity';
import { useUpdateExpenseEntity } from './useUpdateExpenseEntity';

export interface ExpenseCostDialogContentProps {
  entitySpec: ExpenseEntitySpec;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const useEntityForm = (entitySpec: ExpenseEntitySpec) => {
  const entity = useExpenseEntity(entitySpec);
  const stableEntity = useMemoCompare(entity);
  const form = useForm<ExpenseEntity>({
    defaultValues: stableEntity
  });
  useEffect(() => {
    form.reset(stableEntity);
  }, [stableEntity]);
  return form;
};

export const ExpenseCostDialogContent = ({
  entitySpec,
  open,
  onOpenChange
}: ExpenseCostDialogContentProps) => {
  const update = useUpdateExpenseEntity(entitySpec);
  const mutation = promiseState.useMutation(async (request: ExpenseEntity) => {
    const minWait = delay(500);
    await update(form);
    await minWait;
    onOpenChange(false);
  });
  const form = useEntityForm(entitySpec);
  const [tab, setTab] = useState<Tab>(
    form.getValues('actualValue') ? 'actual' : 'estimate'
  );
  useRegisterDirtyState(form.formState.isDirty, {
    message: 'Expense costs form has unsaved changes'
  });
  const summary = calculateExpenseSummary(form.watch());

  const setValue: typeof form.setValue = (name, value, options) => {
    form.setValue(name, value, { ...options, shouldDirty: true });
  };

  const { isValid, isDirty } = form.formState;

  return (
    <FormDialogContent
      open={open}
      disabled={!isValid || !isDirty}
      title="Expense costs"
      mutation={mutation.state}
      onSubmit={form.handleSubmit(data => mutation.track(data))}
      renderHeader={header => (
        <BookingCostsHeader
          title={header}
          estimateTotal={summary.estimate?.total}
          actualTotal={summary.actual?.total}
          tab={tab}
          onTabChange={setTab}
          actualTabName="Expense"
        />
      )}
      footerContent={
        <>
          <DialogErrorFooter mutation={mutation.state} />
          <TotalHeader total={summary[tab]?.total} />
        </>
      }
      renderDialogBody={content => (
        <DialogBody className="p-0">{content}</DialogBody>
      )}
      className="min-w-200 h-[60vh] [--v5-table-bg:var(--color-bg-subtle-2)] p-0"
    >
      {tab === 'estimate' && (
        <BookingCostsSummaryTable
          title="Rate"
          data={[
            {
              total: summary.estimate.total,
              rate: {
                name: 'Estimated rate',
                ...form.watch('estimateValue.rate')
              }
            }
          ]}
          editableMeta={false}
          renderRateFlyover={rate => {
            return (
              <RateEditFlyoverUI
                rate={rate}
                defaultCurrency={form.watch('currency')}
                type={'EXPENSE'}
                onRateChange={rate => {
                  setValue('estimateValue.rate', rate);
                }}
              />
            );
          }}
        />
      )}
      {tab === 'actual' &&
        (summary.actual ? (
          <BookingCostsSummaryTable
            title="Rate"
            data={[
              {
                total: summary.actual.total,
                rate: {
                  name: 'Actual rate',
                  ...form.watch('actualValue.rate')
                }
              }
            ]}
            editableMeta={false}
            renderRateFlyover={rate => {
              return (
                <RateEditFlyoverUI
                  rate={rate}
                  defaultCurrency={form.watch('currency')}
                  type={'EXPENSE'}
                  onRateChange={rate => {
                    setValue('actualValue.rate', rate);
                  }}
                />
              );
            }}
          />
        ) : (
          <div className="px-8 py-12 text-center text-fg-muted">
            No expense added yet. Create an expense to edit the expense costs
            here...
          </div>
        ))}
    </FormDialogContent>
  );
};
